// Cihaz anahtarı üretimi — saf JS, denetlenmiş primitifler (9.34 · D-605).
//
// Kripto MATEMATİĞİ @noble'da (Cure53/Trail of Bits denetimli): eğri, imza,
// rastgelelik. Burada yalnız o primitiflerin çağrısı ve base64 var; kendi eğri
// veya imza kodumuz YOK.
//
// Anahtar şeması (D-605): kimlik Ed25519 (imzalar), signed prekey ve
// tek-kullanımlık prekey X25519 (Diffie-Hellman). Kimlik anahtarının X3DH için
// X25519'a dönüşümü sonraki tuğlanın (oturum kurulumu) işi; burada yalnız üretim
// ve imzalama var.
import { ed25519, x25519 } from '@noble/curves/ed25519.js';
import { randomBytes } from '@noble/hashes/utils.js';

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

// Bayt → standart base64 (dolgulu). Sunucunun biçim kapısı bunu bekliyor
// (base64 alfabesi, 4'ün katı uzunluk).
export function bytesToBase64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const b2 = i + 2 < bytes.length ? bytes[i + 2] : 0;
    out += B64[b0 >> 2];
    out += B64[((b0 & 3) << 4) | (b1 >> 4)];
    out += i + 1 < bytes.length ? B64[((b1 & 15) << 2) | (b2 >> 6)] : '=';
    out += i + 2 < bytes.length ? B64[b2 & 63] : '=';
  }
  return out;
}

export function base64ToBytes(b64: string): Uint8Array {
  const clean = b64.replace(/=+$/, '');
  const out = new Uint8Array(Math.floor((clean.length * 6) / 8));
  let acc = 0;
  let bits = 0;
  let o = 0;
  for (let i = 0; i < clean.length; i++) {
    acc = (acc << 6) | B64.indexOf(clean[i]);
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[o++] = (acc >> bits) & 0xff;
    }
  }
  return out;
}

// UTF-8 — base64 ile aynı gerekçeyle elde (9.34 · D-616).
//
// Hermes `TextEncoder` veriyor ama `TextDecoder` VERMİYOR; React Native de
// eklemiyor. Yani `new TextDecoder()` cihazda ReferenceError'dır: şifreleme
// çalışır, ÇÖZME kalıcı olarak patlardı. Kripto yolu artık hiçbir ortam
// globaline bakmıyor — hangi motorda koştuğu fark etmiyor.

/** Metin → UTF-8 bayt. Eşleşmemiş surrogate, TextEncoder gibi U+FFFD olur. */
export function utf8ToBytes(text: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < text.length; i++) {
    let cp = text.charCodeAt(i);
    if (cp >= 0xd800 && cp <= 0xdbff) {
      const low = i + 1 < text.length ? text.charCodeAt(i + 1) : 0;
      if (low >= 0xdc00 && low <= 0xdfff) {
        cp = 0x10000 + ((cp - 0xd800) << 10) + (low - 0xdc00);
        i++;
      } else {
        cp = 0xfffd;
      }
    } else if (cp >= 0xdc00 && cp <= 0xdfff) {
      cp = 0xfffd;
    }
    if (cp < 0x80) {
      out.push(cp);
    } else if (cp < 0x800) {
      out.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
    } else if (cp < 0x10000) {
      out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    } else {
      out.push(
        0xf0 | (cp >> 18),
        0x80 | ((cp >> 12) & 0x3f),
        0x80 | ((cp >> 6) & 0x3f),
        0x80 | (cp & 0x3f),
      );
    }
  }
  return new Uint8Array(out);
}

/** UTF-8 bayt → metin. Yedek geri yüklemesi MB'larca olabildiği için parçalı. */
export function bytesToUtf8(bytes: Uint8Array): string {
  const units: number[] = [];
  const parcalar: string[] = [];
  let i = 0;
  while (i < bytes.length) {
    const b0 = bytes[i++];
    let cp: number;
    if (b0 < 0x80) {
      cp = b0;
    } else if ((b0 & 0xe0) === 0xc0) {
      cp = ((b0 & 0x1f) << 6) | (bytes[i++] & 0x3f);
    } else if ((b0 & 0xf0) === 0xe0) {
      cp = ((b0 & 0x0f) << 12) | ((bytes[i++] & 0x3f) << 6) | (bytes[i++] & 0x3f);
    } else {
      cp =
        ((b0 & 0x07) << 18) |
        ((bytes[i++] & 0x3f) << 12) |
        ((bytes[i++] & 0x3f) << 6) |
        (bytes[i++] & 0x3f);
    }
    if (cp > 0xffff) {
      cp -= 0x10000;
      units.push(0xd800 + (cp >> 10), 0xdc00 + (cp & 0x3ff));
    } else {
      units.push(cp);
    }
    // Tek seferde apply edilen dizi büyürse yığın taşar; parça parça birleşiyor.
    if (units.length >= 4096) {
      parcalar.push(String.fromCharCode.apply(null, units));
      units.length = 0;
    }
  }
  if (units.length) parcalar.push(String.fromCharCode.apply(null, units));
  return parcalar.join('');
}

export type KeyPairB64 = { publicKey: string; secretKey: string };

/** Ed25519 kimlik çifti. Özel anahtar çağıran tarafından güvenli depoya konur. */
export function generateIdentity(): KeyPairB64 {
  const { secretKey, publicKey } = ed25519.keygen();
  return { publicKey: bytesToBase64(publicKey), secretKey: bytesToBase64(secretKey) };
}

export type SignedPreKeyGen = {
  id: number;
  publicKey: string;
  secretKey: string;
  signature: string;
};

/** X25519 imzalı prekey; açık anahtarı kimlik Ed25519 anahtarıyla imzalanır. */
export function generateSignedPreKey(id: number, identitySecretB64: string): SignedPreKeyGen {
  const { secretKey, publicKey } = x25519.keygen();
  const signature = ed25519.sign(publicKey, base64ToBytes(identitySecretB64));
  return {
    id,
    publicKey: bytesToBase64(publicKey),
    secretKey: bytesToBase64(secretKey),
    signature: bytesToBase64(signature),
  };
}

export type OneTimePreKeyGen = { id: number; publicKey: string; secretKey: string };

/** Bir demet X25519 tek-kullanımlık prekey. Her yeni oturum birini tüketir. */
export function generateOneTimePreKeys(startId: number, count: number): OneTimePreKeyGen[] {
  const out: OneTimePreKeyGen[] = [];
  for (let i = 0; i < count; i++) {
    const { secretKey, publicKey } = x25519.keygen();
    out.push({ id: startId + i, publicKey: bytesToBase64(publicKey), secretKey: bytesToBase64(secretKey) });
  }
  return out;
}

// Signal gibi 14 bitlik registration id (1..16380). Cihaz başına bir kez üretilip
// kimlikle birlikte saklanır.
export function generateRegistrationId(): number {
  const b = randomBytes(2);
  return (((b[0] << 8) | b[1]) % 16380) + 1;
}

/**
 * Var olan bir imzalı prekey'in açık anahtarını kimlik anahtarıyla imzala.
 * Demeti yeniden yüklerken (tek-kullanımlık tazeleme) gerekiyor: imzalı prekey
 * DEĞİŞTİRİLMEMELİ, ama gövdede imzası yine gönderilmeli. Ed25519 deterministik
 * olduğu için sonuç ilk kayıttaki imzanın aynısıdır.
 */
export function signPreKey(identitySecretB64: string, preKeyPublicB64: string): string {
  return bytesToBase64(
    ed25519.sign(base64ToBytes(preKeyPublicB64), base64ToBytes(identitySecretB64)),
  );
}

/** X25519 özel anahtardan açık anahtarı türet (alıcı, sakladığı prekey özelinden
 *  eşleşen açık anahtarı kurmak için kullanır). */
export function x25519PublicKey(secretB64: string): string {
  return bytesToBase64(x25519.getPublicKey(base64ToBytes(secretB64)));
}

/** İmzalı prekey imzasını kimlik açık anahtarıyla doğrula (test ve karşı taraf için). */
export function verifySignedPreKey(
  identityPublicB64: string,
  signedPreKeyPublicB64: string,
  signatureB64: string,
): boolean {
  return ed25519.verify(
    base64ToBytes(signatureB64),
    base64ToBytes(signedPreKeyPublicB64),
    base64ToBytes(identityPublicB64),
  );
}
